import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '../components/ui/button';
import { ArrowRight, Phone, ChevronDown, ChevronLeft, ChevronRight, Calendar, MapPin } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { proximoEvento, modalidadLabel, rolLabel, formatFechaLarga } from '../data/eventos';
import './Home.css';

const Home = () => {
  const navigate = useNavigate();
  const whatsappNumber = '5493413047536';
  const whatsappLink = `https://wa.me/${whatsappNumber}?text=\u00a1Hola!`;
  const evento = proximoEvento();

  useEffect(() => {
    document.documentElement.classList.add('home-snap');
    document.body.classList.add('home-snap');
    return () => {
      document.documentElement.classList.remove('home-snap');
      document.body.classList.remove('home-snap');
    };
  }, []);

  const representaciones = [
    {
      id: 1,
      title: 'KSB',
      logo: '/assets/logos/KSB-LOGO.webp',
      bgColor: '#F8F7F4',
      light: true,
      description: 'Líder mundial en bombas, válvulas y sistemas de control para agua, industria y energía.',
      scrollTo: 'productos-ksb-section'
    },
    {
      id: 2,
      title: 'TECNAL',
      logo: '/assets/logos/TECNAL-LOGO.webp',
      bgColor: '#F8F7F4',
      light: true,
      description: 'Equipos para plantas aceiteras, bioetanol, alimento balanceado, almacenamiento de granos y movimiento portuario.',
      scrollTo: 'equipos-tecnal-section'
    }
  ];

  const productosKSB = [
    {
      id: 1,
      title: 'Bombas centrífugas y de proceso',
      description: 'Equipos para impulsión, transporte y manejo de fluidos industriales con máxima fiabilidad y eficiencia energética.',
      image: '/assets/ksb/aguas-efluentes/328bd827-3308-4ede-b246-daabb5d7e1f4.webp',
      href: '/ksb/bombas'
    },
    {
      id: 2,
      title: 'Válvulas industriales',
      description: 'Control seguro y eficiente para líneas de producción y sistemas de alta presión.',
      image: '/assets/ksb/valvulas/IMG_2580.webp',
      href: '/ksb/valvulas'
    },
    {
      id: 3,
      title: 'Sistemas de presurización (Hyamat)',
      description: 'Soluciones automáticas para mantener presión constante en redes de distribución de agua.',
      image: '/assets/ksb/presurizacion/IMG_3503.webp',
      href: '/ksb/presurizacion'
    },
    {
      id: 4,
      title: 'Monitoreo inteligente (KSB Guard)',
      description: 'Sensores conectados para vigilar bombas en tiempo real y prevenir fallas.',
      svgType: 'monitor',
      href: '/ksb'
    }
  ];

  const CLIENT_LOGOS = [
    'YPF', 'CARGILL', 'BUNGE', 'COFCO', 'ADM', 'VITERRA', 'LDC',
    'MOLINOS-AGRO', 'AGD', 'ACA', 'PETROBRAS', 'PAMPA-ENERGIA', 'SIEMENS',
    'GLENCORE', 'PROFERTIL', 'DOW-QUIMICA', 'AGUAS-SANTAFESINAS', 'GERDAU',
    'AMBEV', 'UNILEVER'
  ];

  const equiposTecnal = [
    {
      id: 1,
      title: 'Quebradores',
      description: 'Quebradora de granos oleaginosos. Hasta 1.500 TPD, 4 a 8 rodillos acanalados.',
      image: '/assets/tecnal/crackers_cover.webp',
      href: '/tecnal/aceite-vegetal/quebrador'
    },
    {
      id: 2,
      title: 'Expanders',
      description: 'El expander más grande del mundo. Hasta 4.000 TPD en base soja.',
      image: '/assets/tecnal/expanders_cover.webp',
      href: '/tecnal/aceite-vegetal/expander'
    },
    {
      id: 3,
      title: 'Laminadores',
      description: 'Laminador de semillas oleaginosas. Capacidad de 200 a 500 TPD.',
      image: '/assets/tecnal/flakers_cover.webp',
      href: '/tecnal/aceite-vegetal/laminador'
    },
    {
      id: 4,
      title: 'Extractor rotativo',
      description: 'Extractor de aceite por celdas rotativas de fondo fijo. Alta eficiencia y bajo consumo.',
      image: '/assets/tecnal/extractor_cover.webp',
      href: '/tecnal/aceite-vegetal/extractor-rotativo'
    },
    {
      id: 5,
      title: 'Secadora Vertical',
      description: 'Capacidad de 15 a 300 TPD. Sistema modular y autoportante con doble aprovechamiento del aire caliente para máxima eficiencia energética.',
      image: '/assets/tecnal/ag/tecnal_ag_secadora-vertical_1.webp',
      href: '/tecnal/almacenamiento-de-granos/secadora-vertical'
    }
  ];

  const MonitorSvg = () => (
    <svg width="100%" height="160" viewBox="0 0 400 160" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
      <rect width="400" height="160" fill="#1A3A5C" />
      {/* Radar circles */}
      <circle cx="200" cy="80" r="60" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
      <circle cx="200" cy="80" r="45" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="1" />
      <circle cx="200" cy="80" r="30" fill="none" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />
      <circle cx="200" cy="80" r="15" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
      <circle cx="200" cy="80" r="3" fill="rgba(255,255,255,0.3)" />
      {/* Signal lines */}
      <path d="M170 55 Q 160 40, 145 38" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M163 48 Q 155 36, 142 34" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M230 105 Q 240 120, 255 122" fill="none" stroke="rgba(255,255,255,0.12)" strokeWidth="1.5" strokeLinecap="round" />
      <path d="M237 112 Q 245 124, 258 126" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1.5" strokeLinecap="round" />
      {/* Network nodes */}
      <circle cx="145" cy="50" r="3" fill="rgba(255,255,255,0.25)" />
      <circle cx="260" cy="110" r="3" fill="rgba(255,255,255,0.25)" />
      <circle cx="240" cy="45" r="2.5" fill="rgba(255,255,255,0.2)" />
      <circle cx="155" cy="115" r="2.5" fill="rgba(255,255,255,0.2)" />
      <circle cx="120" cy="85" r="2" fill="rgba(255,255,255,0.15)" />
      <circle cx="280" cy="75" r="2" fill="rgba(255,255,255,0.15)" />
      {/* Connecting lines */}
      <line x1="145" y1="50" x2="185" y2="68" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
      <line x1="260" y1="110" x2="218" y2="93" stroke="rgba(255,255,255,0.08)" strokeWidth="1" />
      <line x1="240" y1="45" x2="212" y2="66" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
      <line x1="155" y1="115" x2="187" y2="93" stroke="rgba(255,255,255,0.06)" strokeWidth="1" />
      <line x1="120" y1="85" x2="170" y2="80" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
      <line x1="280" y1="75" x2="230" y2="80" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
    </svg>
  );

  const scrollToRepresentaciones = () => {
    const element = document.getElementById('representaciones-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const Carousel = ({ children }) => {
    const ref = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);

    const checkScroll = useCallback(() => {
      const el = ref.current;
      if (!el) return;
      setCanScrollLeft(el.scrollLeft > 1);
      setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
    }, []);

    useEffect(() => {
      const el = ref.current;
      if (!el) return;
      checkScroll();
      el.addEventListener('scroll', checkScroll, { passive: true });
      window.addEventListener('resize', checkScroll);
      return () => {
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
      };
    }, [checkScroll]);

    const scroll = (direction) => {
      const el = ref.current;
      if (!el) return;
      const card = el.querySelector('.home-card');
      if (!card) return;
      const distance = card.offsetWidth + 24; // card width + gap
      el.scrollBy({ left: direction * distance, behavior: 'smooth' });
    };

    return (
      <div className="carousel-wrapper">
        {canScrollLeft && (
          <button className="carousel-arrow carousel-arrow--left" onClick={() => scroll(-1)} aria-label="Anterior">
            <ChevronLeft size={22} />
          </button>
        )}
        <div className="home-cards-carousel" ref={ref}>
          {children}
        </div>
        {canScrollRight && (
          <button className="carousel-arrow carousel-arrow--right" onClick={() => scroll(1)} aria-label="Siguiente">
            <ChevronRight size={22} />
          </button>
        )}
      </div>
    );
  };

  return (
    <>
    <div className="home-container">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg">
          <img
            src="/assets/ksb/aguas-efluentes/IMG_2628.webp"
            alt=""
            className="hero-bg__img"
            loading="eager"
          />
          <div className="hero-bg__overlay" />
          <div className="hero-bg__glow" />
        </div>
        <div className="hero-content">
          <p className="hero-eyebrow">Excelencia aplicada a la ingeniería</p>
          <h1 className="hero-title">
            Más de 35 años impulsando el desarrollo industrial
          </h1>
          <p className="hero-description">
            Representamos a marcas líderes internacionales en tecnología industrial,
            ofreciendo soluciones confiables y eficientes para cada sector productivo.
          </p>
          <button
            className="hero-scroll-indicator"
            onClick={scrollToRepresentaciones}
            aria-label="Ver Representaciones"
          >
            <ChevronDown size={32} />
          </button>
        </div>
      </section>

      {/* Representaciones Section */}
      <section id="representaciones-section" className="representaciones-section">
        <div className="container">
          <h2 className="section-title">Nuestras Representaciones</h2>

          <div className="representaciones-grid">
            {representaciones.map((rep) => (
              <div key={rep.id} className={`rep-card${rep.light ? ' rep-card--light' : ''}`} style={{ backgroundColor: rep.bgColor }} onClick={() => document.getElementById(rep.scrollTo)?.scrollIntoView({ behavior: 'smooth' })}>
                <div className="rep-card__logo-area">
                  <img src={rep.logo} alt={`${rep.title} logo`} loading="lazy" className="rep-card__logo-img" />
                </div>
                <div className="rep-card__content">
                  <p className="rep-card__desc">{rep.description}</p>
                  <span className="rep-card__btn">Ver productos →</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Productos KSB Section */}
      <section id="productos-ksb-section" className="productos-ksb-section">
        <div className="container">
          <p className="section-eyebrow">Productos de KSB</p>

          <Carousel>
            {productosKSB.map((producto) => (
              <div
                key={producto.id}
                className="home-card home-card--ksb"
                onClick={() => navigate(producto.href)}
              >
                <div className="home-card__image">
                  {producto.svgType === 'monitor' ? (
                    <MonitorSvg />
                  ) : (
                    <>
                      <img src={producto.image} alt={producto.title} loading="lazy" />
                      <div className="home-card__gradient" />
                    </>
                  )}
                </div>
                <div className="home-card__body">
                  <h3 className="home-card__title">{producto.title}</h3>
                  <p className="home-card__desc">{producto.description}</p>
                </div>
              </div>
            ))}
          </Carousel>

          <div className="section-cta">
            <Button
              size="lg"
              className="section-cta-primary"
              onClick={() => navigate('/ksb')}
            >
              Ver todos los productos de KSB
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        </div>
      </section>

      {/* Equipos TECNAL Section */}
      <section id="equipos-tecnal-section" className="equipos-tecnal-section">
        <div className="container">
          <p className="section-eyebrow">Equipos destacados de TECNAL</p>

          <Carousel>
            {equiposTecnal.map((equipo) => (
              <div
                key={equipo.id}
                className="home-card home-card--tecnal"
                onClick={() => navigate(equipo.href)}
              >
                <div className="home-card__image">
                  <img src={equipo.image} alt={equipo.title} loading="lazy" />
                  <div className="home-card__gradient home-card__gradient--tecnal" />
                </div>
                <div className="home-card__body">
                  <h3 className="home-card__title">{equipo.title}</h3>
                  <p className="home-card__desc">{equipo.description}</p>
                </div>
              </div>
            ))}
          </Carousel>

          <div className="section-cta">
            <Button
              size="lg"
              className="section-cta-primary"
              onClick={() => navigate('/tecnal')}
            >
              Ver todos los equipos de TECNAL
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        </div>
      </section>

      {/* Sobre Nosotros Section */}
      <section className="sobre-nosotros-section">
        <div className="container">
          <h2 className="section-title">Sobre Nosotros</h2>
          <p className="section-subtitle">
            Desde 1991, acompañamos el crecimiento de la industria en Argentina.
          </p>
          <p className="section-text">
            Trabajamos como nexo entre los fabricantes líderes y las empresas industriales de la región, 
            ofreciendo equipos confiables y soporte especializado para plantas productivas de distintos sectores.
            <br /><br />
            Nuestra misión es acercar a las industrias locales la tecnología y calidad de marcas internacionales 
            consolidadas, contribuyendo a mejorar sus procesos y su eficiencia operativa.
          </p>
          <Button 
            size="lg" 
            className="section-cta-button"
            onClick={() => window.open(whatsappLink, '_blank')}
          >
            <Phone className="mr-2" size={20} />
            Contactanos
          </Button>
        </div>
      </section>

      {/* Próximo evento Section */}
      {evento && (
        <section className="proximo-evento-section">
          <div className="container">
            <p className="section-eyebrow proximo-evento__eyebrow">Próximo evento</p>
            <div className="proximo-evento-card">
              <div className="proximo-evento-card__main">
                <div className="proximo-evento-card__badges">
                  <span className={`evento-badge evento-badge--modalidad evento-badge--${evento.modalidad}`}>
                    <MapPin size={13} />
                    {modalidadLabel(evento.modalidad)}
                  </span>
                  {evento.rol && (
                    <span className={`evento-badge evento-badge--rol evento-badge--rol-${evento.rol}`}>
                      {rolLabel(evento.rol)}
                    </span>
                  )}
                </div>
                <p className="proximo-evento-card__fecha">
                  <Calendar size={16} />
                  {formatFechaLarga(evento.fecha)}
                </p>
                <h3 className="proximo-evento-card__titulo">{evento.titulo}</h3>
                <p className="proximo-evento-card__organizador">
                  Organiza: <strong>{evento.organizador}</strong>
                </p>
              </div>
              <Button
                size="lg"
                className="section-cta-primary proximo-evento-card__cta"
                onClick={() => navigate('/eventos')}
              >
                Ver todos los eventos
                <ArrowRight className="ml-2" size={18} />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Clientes Section */}
      <section className="clientes-section">
        <div className="container">
          <h2 className="section-title">Nuestros Clientes</h2>
          <p className="section-text">
            <strong>Más de 200 empresas</strong> nos eligen por nuestra trayectoria,
            compromiso y la calidad de nuestras soluciones.
          </p>
        </div>

        <div className="clients-marquee" aria-hidden="true">
          <div className="clients-marquee__track">
            {[...CLIENT_LOGOS, ...CLIENT_LOGOS].map((c, i) => (
              <img
                key={`${c}-${i}`}
                src={`/assets/logos/clientes/${c}-LOGO.webp`}
                alt={c}
                loading="lazy"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            ))}
          </div>
        </div>

        <div className="container">
          <Button
            size="lg"
            className="section-cta-primary"
            onClick={() => navigate('/clientes')}
          >
            Ver todos nuestros clientes
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </section>
    </div>

    </>
  );
};

export default Home;